import { useTheme } from "next-themes";
import styles from "../../styles/transaction-details.module.css";
import { useAuth } from "../../store/AuthContext";
import dayjs from "dayjs";
import { format2, numberWithCommas, separateNumberWithoutFormat } from "../../utils/numbers";

import utc from "dayjs/plugin/utc";
import { getUnitholderDetails } from "../../services/auth.service";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
dayjs.extend(utc);

function formatToUSDate(input) {
  return dayjs.utc(input).format("MMM D, YYYY");
}

const TransactionDetail = ({ data, txnType }) => {
  const [transfree, setTransfree] = useState(() => ({
    loading: false,
    data: null,
    error: false,
    isTransferIn: false,
  }));
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();

  const isDarkMode = theme === "dark";

  const units = separateNumberWithoutFormat(data?.txnData?.units || "");
  const grossAmount = separateNumberWithoutFormat(data?.txnData?.gross_amount || "");
  const netAmount = separateNumberWithoutFormat(data?.txnData?.net_amount || "");

  const isTransferOfUnits = data?.txnData?.txnr_type === "transferofunits";

  const transfreeData = useCallback(async () => {
    if (isTransferOfUnits) {
      const transfrees_data = JSON.parse(data?.txnData?.transfrees)[0];

      if (data?.txnData?.folio_no !== user?.data.folio_no) {
        setTransfree((prev) => ({ ...prev, loading: true, isTransferIn: true }));

        try {
          const response = await getUnitholderDetails(user?.data.email, data?.txnData?.folio_no);

          // console.log(response);

          if (!response.error) {
            setTransfree((prev) => ({ ...prev, error: false, data: { ...response.data[0] } }));
          }
        } catch (err) {
          // console.log(err);
          setTransfree((prev) => ({ ...prev, error: true }));
        } finally {
          setTransfree((prev) => ({ ...prev, loading: false }));
        }
      } else {
        setTransfree((prev) => ({ data: transfrees_data, loading: false, error: false, isTransferIn: false }));
      }
    }
  }, [data]);

  const getTransactionDetails = () => {
    if (data?.txnData.txnr_type === "redemptionofunits" || data?.txnData?.txnr_type === "conversionout") {
      return {
        load: separateNumberWithoutFormat(data?.txnData?.redemption_load || ""),
        tax: separateNumberWithoutFormat(data?.txnData?.tax_cgt || ""),
      };
    } else if (data?.txnData?.txnr_type === "saleofunit") {
      return {
        load: separateNumberWithoutFormat(data?.txnData?.sale_load || ""),
      };
    } else if (data?.txnData?.txnr_type === "conversionin") {
      return {
        load: separateNumberWithoutFormat(data?.txnData?.sale_load || ""),
      };
    } else if (data?.txnData?.txnr_type === "transferofunits ") {
      return null;
    }
  };

  const appliedLoad = getTransactionDetails();

  const safeData = data ? data : "saleofunits";

  useEffect(() => {
    if (isTransferOfUnits) {
      transfreeData();
    }
  }, [data]);

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}>
      {/* <div className={styles.themeToggle}>
        <button onClick={toggleTheme} className={styles.toggleButton}>
          {isDarkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
      </div> */}

      <div className={styles.receipt}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={28}
                height={28}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-up'
                style={{
                  color: isDarkMode ? "rgb(205, 205, 205)" : "",
                }}
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M12 5l0 14' />
                <path d='M18 11l-6 -6' />
                <path d='M6 11l6 -6' />
              </svg>
            </div>
            <div className={styles.companyInfo}>
              <h1>{data?.fund_code}</h1>
              <p>{data?.fund_name}</p>
            </div>
          </div>
          <div className={styles.receiptTitle}>
            <h2>{formatToUSDate(data?.txnData?.settlement_date)}</h2>
            <div className={styles.status}>
              <span
                style={{
                  fontSize: "12px",
                  marginInlineEnd: "6px",
                  opacity: ".8",
                  letterSpacing: ".02em",
                }}
              >
                {t("modals.transaction.summary.entry")}
              </span>
              {formatToUSDate(data?.txnData?.last_updated_at)}
            </div>
            {/* <div className={styles.status}>
              <span className={styles.statusBadge}>COMPLETED</span>
            </div> */}
          </div>
        </div>

        {/* Transaction Summary */}
        <div className={styles.section}>
          <h3>{t("modals.transaction.summary.title")}</h3>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>{t("modals.transaction.summary.type")}</div>
              <div className={styles.cardValue}>{txnType(safeData?.txnData || safeData, user)}</div>
              <span className={styles.time}>#{data?.txnData?.txn_id}</span>
            </div>
            {/* <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>Transaction ID</div>
              <div className={styles.cardValue}>TXN-1747197660997</div>
            </div> */}
            {/* <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>Date</div>
              <div className={styles.cardValue}>{formatToUSDate(data?.last_updated_at)}</div>
              <span className={styles.time}>Entry: {formatToUSDate(data?.settlement_date)}</span>
            </div> */}
            {/* <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>Fund</div>
              <div
                className={styles.cardValue}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                }}
              >
                {data?.fund_code}
              </div>
              <span className={styles.time + ' text-start'} style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
              }}>{data?.fund_name}</span>
            </div> */}
            <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>{t("modals.transaction.summary.price")}</div>
              <div className={styles.cardValue}>
                <span
                  style={{
                    fontSize: "14px",
                    marginRight: "4px",
                  }}
                >
                  Rs
                </span>
                {format2(data?.txnData.nav ?? data?.txnData.offer_price)}
              </div>
              <span className={styles.time}>As on {formatToUSDate(data?.txnData?.settlement_date)}</span>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>{t("modals.transaction.summary.units")}</div>
              <div className={styles.cardValue}>
                {numberWithCommas(units.whole)}
                {units.hasDecimal ? (
                  <>
                    .
                    <span
                      style={{
                        fontSize: "15px",
                      }}
                    >
                      {units.decimal}
                    </span>
                  </>
                ) : (
                  ""
                )}
              </div>
              {/* <span className={styles.time} style={{ fontSize: "14px" }}>
                units
              </span> */}
            </div>
            {/* <div className={styles.summaryCard}>
              <div className={styles.cardHeader}>Settlement Date</div>
              <div className={styles.cardValue}>Jan 17, 2024</div>
            </div> */}
          </div>
        </div>

        {/* Fund Details */}
        {/* <div className={styles.section}>
          <h3>Fund Information</h3>
          <div className={styles.fundDetails}>
            <div className={styles.fundHeader}>
              <div className={styles.fundName}>
                <h4>Fund Money Market - FMM</h4>
                <p className={styles.isin}>Asset Allocation Fund</p>
              </div>
              <div className={styles.fundRating}>
                <div className={styles.ratingStars}>★★★★☆</div>
                <span>Medium</span>
              </div>
            </div>
            <div className={styles.navInfo}>
              <div className={styles.navItem}>
                <span>NAV per Unit</span>
                <span className={styles.navValue}>
                  <span
                    style={{
                      fontSize: "14px",
                      marginRight: "4px",
                    }}
                  >
                    Rs
                  </span>
                  127.45
                </span>
              </div>
              <div className={styles.navItem}>
                <span>NAV Date</span>
                <span>Jan 15, 2024</span>
              </div>
            </div>
          </div>
        </div> */}

        {/* Transaction Details */}
        <div className={styles.section}>
          {isTransferOfUnits ? (
            transfree.loading ? (
              <>
                <h3>{data?.txnData?.folio_no === user?.data.folio_no ? t("modals.transaction.transfree.to") : t("modals.transaction.transfree.by")}</h3>
                <div className={styles.accountGrid}>
                  <div
                    className='w-100 position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-3'
                    style={{
                      color: theme === "light" ? "rgb(0,0,0)" : "rgb(255,255,255)",
                    }}
                  >
                    <div
                      className=''
                      role='status'
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "300",
                        letterSpacing: ".02em",
                      }}
                    >
                      {t("app.loading.transfree")}
                    </div>
                    <div
                      className='spinner-border'
                      style={{
                        fontSize: "11px",
                        height: "24px",
                        width: "24px",
                        color: theme === "light" ? "var(--theme-light-primary)" : "var(--theme-light-secondary)",
                      }}
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                </div>
              </>
            ) : transfree.error ? (
              <>
                <h3>{data?.txnData?.folio_no === user?.data.folio_no ? t("modals.transaction.transfree.to") : t("modals.transaction.transfree.by")}</h3>
                <div className={styles.accountGrid}>
                  <div className='position-absolute start-50 top-50 translate-middle-x'>{t("app.messages.no.transfree")}</div>
                </div>
              </>
            ) : (
              <>
                <h3>{data?.txnData?.folio_no === user?.data.folio_no ? t("modals.transaction.transfree.to") : t("modals.transaction.transfree.by")}</h3>
                <div className={styles.accountGrid}>
                  <div className={styles.accountItem}>
                    <span className={styles.label}>{t("modals.transaction.transfree.account")}</span>
                    <span className={styles.value}>{transfree.isTransferIn ? transfree.data?.name : transfree.data?.unitholder_name}</span>
                  </div>
                  {/* <div className={styles.accountItem}>
                    <span className={styles.label}>Transferred Units</span>
                    <span className={styles.value}>{transfree.isTransferIn ? data?.txnData?.units : transfree.data?.transferees_unit_transfer}</span>
                  </div> */}
                  <div className={styles.accountItem}>
                    <span className={styles.label}>{t("modals.transaction.transfree.folio")}</span>
                    <span className={styles.value}>{transfree.data?.folio_no || transfree.data?.main_folio_no}</span>
                  </div>
                  {/* <div className={styles.accountItem}>
                      <span className={styles.label}>Payment Method</span>
                      <span className={styles.value}>Bank Transfer (****4567)</span>
                    </div> */}
                </div>
              </>
            )
          ) : (
            <>
              <h3>{t("modals.transaction.amount.title")}</h3>
              <div className={styles.transactionTable}>
                <div className={styles.tableRow}>
                  <span>{t("modals.transaction.amount.gross")}</span>
                  <span className={styles.amount}>
                    <span
                      style={{
                        fontSize: "13px",
                        marginRight: "7px",
                      }}
                    >
                      Rs
                    </span>
                    {numberWithCommas(grossAmount.whole)}
                    {grossAmount.hasDecimal ? (
                      <>
                        .
                        <span
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {grossAmount.decimal}
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </span>
                </div>
                {/* <div className={styles.tableRow}>
                  <span>NAV per Unit</span>
                  <span>
                      <span style={{
                        fontSize: '14px',
                        marginRight: '4px',
                      }}>Rs</span>
                    127.45
                  </span>
                </div>
                <div className={styles.tableRow}>
                  <span>Units Allocated</span>
                  <span className={styles.units}>196.1538</span>
                </div> */}
                {appliedLoad && Object.keys(appliedLoad).length > 0 ? (
                  <>
                    <div className={styles.divider}></div>
                    {Object.keys(appliedLoad).map((key, index) => {
                      let name = "";

                      if (key === "load") {
                        if (data?.txnData?.txnr_type === "saleofunit") {
                          name = t("modals.transaction.amount.sales");
                        } else if (data?.txnData?.txnr_type === "conversionin") {
                          name = t("modals.transaction.amount.sales");
                        } else if (data?.txnData?.txnr_type === "redemptionofunits" || data?.txnData?.txnr_type === "conversionout") {
                          name = t("modals.transaction.amount.redemption");
                        }
                      } else if (key === "tax") {
                        name = t("modals.transaction.amount.tax");
                      }

                      const item = appliedLoad[key];

                      return (
                        <div className={styles.tableRow} key={index}>
                          <span>{name.length > 0 ? name : ""}</span>
                          <span className={styles.fee}>
                            <span
                              style={{
                                fontSize: "13px",
                                marginRight: "7px",
                              }}
                            >
                              Rs
                            </span>
                            {numberWithCommas(item.whole)}
                            {item.hasDecimal ? (
                              <>
                                .
                                <span
                                  style={{
                                    fontSize: "12px",
                                  }}
                                >
                                  {item.decimal}
                                </span>
                              </>
                            ) : (
                              ""
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  ""
                )}

                {/* <div className={styles.tableRow}>
                  <span>Processing Fee</span>
                  <span className={styles.fee}>
                      <span style={{
                        fontSize: '14px',
                        marginRight: '4px',
                      }}>Rs</span>
    15.00</span>
                </div> */}
                <div className={styles.divider}></div>
                <div className={`${styles.tableRow} ${styles.totalRow}`}>
                  <span>{t("modals.transaction.amount.net")}</span>
                  <span className={styles.totalAmount}>
                    <span
                      style={{
                        fontSize: "14px",
                        marginRight: "7px",
                      }}
                    >
                      Rs
                    </span>
                    {numberWithCommas(netAmount.whole)}
                    {netAmount.hasDecimal ? (
                      <>
                        .
                        <span
                          style={{
                            fontSize: "15px",
                          }}
                        >
                          {netAmount.decimal}
                        </span>
                      </>
                    ) : (
                      ""
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Account Information */}
        {/* <div className={styles.section}>
          <h3>Account Information</h3>
          <div className={styles.accountGrid}>
            <div className={styles.accountItem}>
              <span className={styles.label}>Account Holder</span>
              <span className={styles.value}>Ali Hassan</span>
            </div>
            <div className={styles.accountItem}>
              <span className={styles.label}>Account Number</span>
              <span className={styles.value}>PK 3244234324324</span>
            </div>
            <div className={styles.accountItem}>
              <span className={styles.label}>Folio Number</span>
              <span className={styles.value}>DL-992</span>
            </div>
            <div className={styles.accountItem}>
              <span className={styles.label}>Payment Method</span>
              <span className={styles.value}>Bank Transfer (****4567)</span>
            </div>
          </div>
        </div> */}

        {/* Portfolio Summary */}
        {/* <div className={styles.section}>
          <h3>Updated Portfolio Summary</h3>
          <div className={styles.portfolioSummary}>
            <div className={styles.portfolioItem}>
              <span>Total Units Held</span>
              <span className={styles.portfolioValue}>1,847.3421 units</span>
            </div>
            <div className={styles.portfolioItem}>
              <span>Current Value</span>
              <span className={styles.portfolioValue}>
                <span
                  style={{
                    fontSize: "14px",
                    marginRight: "4px",
                    color: isDarkMode ? "rgb(180,180,180)" : "",
                  }}
                >
                  Rs
                </span>
                235,456.78
              </span>
            </div>
            <div className={styles.portfolioItem}>
              <span>Average Cost per Unit</span>
              <span className={styles.portfolioValue}>
                <span
                  style={{
                    fontSize: "14px",
                    marginRight: "4px",
                    color: isDarkMode ? "rgb(180,180,180)" : "",
                  }}
                >
                  Rs
                </span>
                124.32
              </span>
            </div>
            <div className={styles.portfolioItem}>
              <span>Unrealized Gain/Loss</span>
              <span className={`${styles.portfolioValue} ${styles.gain}`}>
                +
                <span
                  style={{
                    fontSize: "14px",
                    marginRight: "4px",
                  }}
                >
                  Rs
                </span>
                5,789.45 (+2.52%)
              </span>
            </div>
          </div>
        </div> */}

        {/* Important Notes */}
        {/* <div className={styles.section}>
          <h3>Important Information</h3>
          <div className={styles.notes}>
            <div className={styles.noteItem}>
              <div className={styles.noteIcon}>ℹ️</div>
              <div className={styles.noteText}>
                <strong>Settlement:</strong> Units will be credited to your account on the settlement date.
              </div>
            </div>
            {/* <div className={styles.noteItem}>
              <div className={styles.noteIcon}>📧</div>
              <div className={styles.noteText}>
                <strong>Confirmation:</strong> A detailed statement will be sent to your registered email address.
              </div>
            </div> */}
        {/* <div className={styles.noteItem}>
              <div className={styles.noteIcon}>⚠️</div>
              <div className={styles.noteText}>
                <strong>Risk Disclosure:</strong> Mutual fund investments are subject to market risks. Please read the scheme information document carefully.
              </div>
            </div>
          </div>
        </div> */}

        {/* Footer */}
        {/* <div className={styles.footer}>
          <div className={styles.footerContent}>
            <div className={styles.contactInfo}>
              <h4>Contact Information</h4>
              <p>📞 1-800-FUND-HELP (1-800-386-3435)</p>
              <p>📧 support@oneapp.com</p>
              <p>🌐 www.oneapp.com</p>
            </div>
            <div className={styles.legalInfo}>
              <p className={styles.disclaimer}>
                This is a computer-generated receipt and does not require a signature. OneApp is regulated by the Securities and Exchange Commission.
              </p>
              <p className={styles.printInfo}>Receipt generated on: {new Date().toLocaleString()}</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default TransactionDetail;
